import {
  Box,
  Button,
  Flex,
  Icon,
  Popover,
  PopoverAnchor,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FiChevronLeft, FiFilter } from "react-icons/fi";
import {
  type ContractFieldFacetDto,
  ContractFieldFilterKindDto,
  useSearchOrganizationsAndEntriesQuery,
} from "../../../autogen/bff-api";
import { RemovableFilterChip } from "../../components/RemovableFilterChip";
import { useDisplayDate } from "../../formatting/useDisplayDate";
import type { SelectorValue } from "../../input/Selector/SelectorValue";
import { NORWEGIAN_COUNTIES } from "../../procurement/norwegianCounties";
import { CheckboxListFilter } from "./CheckboxListFilter";
import {
  EMPTY_PERIOD,
  LowestValueInput,
  type Period,
  PeriodInput,
} from "./ContractRangeInputs";
import { RelationFilter } from "./RelationFilter";
import {
  contractFilterKey,
  hasFilterValue,
  type SupplierFilterKey,
  withoutFilter,
} from "./supplierFilterKeys";
import { NO_SUPPLIER_FILTERS, type SupplierFilters } from "./supplierFilters";
import { useSupplierRelationLabels } from "./useSupplierRelationLabels";

type Group = "relation" | "register" | "contracts";

interface Definition {
  key: SupplierFilterKey;
  group: Group;
  label: string;
  chipName: string;
  chipValue: string;
  editor: ReactNode;
}

interface Props {
  value: SupplierFilters;
  onChange: (filters: SupplierFilters) => void;
  search?: ReactNode;
  size?: "sm" | "md";
}

const RANGE_KINDS: ContractFieldFilterKindDto[] = [
  ContractFieldFilterKindDto.Number,
  ContractFieldFilterKindDto.Amount,
  ContractFieldFilterKindDto.Percentage,
];

export const SupplierFilterBar = ({
  value,
  onChange,
  search,
  size = "sm",
}: Props) => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const relationLabels = useSupplierRelationLabels();
  const filterMenu = useDisclosure();
  const [picked, setPicked] = useState<SupplierFilterKey | null>(null);
  const [editing, setEditing] = useState<SupplierFilterKey | null>(null);

  const {
    data: facetsResponse,
    isLoading: facetsLoading,
    isError: facetsFailed,
    refetch: refetchFacets,
  } = useSearchOrganizationsAndEntriesQuery({ limit: 1, withFacets: true });
  const facets = facetsResponse?.facets;

  const countyOptions = useMemo(() => {
    const registered = new Set(
      facets?.municipalities.map((m) => m.countyNumber),
    );
    return NORWEGIAN_COUNTIES.filter((county) => registered.has(county.number))
      .sort((a, b) => a.name.localeCompare(b.name, "nb"))
      .map((county) => ({ value: county.number, label: county.name }));
  }, [facets]);
  const municipalityOptions = useMemo(
    () =>
      (facets?.municipalities ?? [])
        .filter(
          (m) =>
            value.counties.length === 0 ||
            value.counties.includes(m.countyNumber),
        )
        .map((m) => ({ value: m.number, label: m.name ?? m.number })),
    [facets, value.counties],
  );
  const naceOptions = useMemo(
    () =>
      (facets?.naceCodes ?? []).map((n) => ({
        value: n.code,
        label: n.description ? `${n.code} ${n.description}` : n.code,
      })),
    [facets],
  );

  const labelsOf = (chosen: string[], options: SelectorValue[]) =>
    chosen
      .map((v) => options.find((option) => option.value === v)?.label ?? v)
      .join(", ");
  const periodLabel = (period: Period) =>
    `${period.from ? displayDate(period.from) : "…"} – ${period.to ? displayDate(period.to) : "…"}`;

  const listDefinition = (
    key: SupplierFilterKey,
    group: Group,
    label: string,
    chosen: string[],
    options: SelectorValue[],
    set: (chosen: string[]) => void,
    emptyMessage: string,
    chipName = label,
  ): Definition => ({
    key,
    group,
    label,
    chipName: chipName,
        chipValue: labelsOf(chosen, options),
    editor: (
      <CheckboxListFilter
        label={label}
        options={options}
        chosen={chosen}
        onChange={set}
        isLoading={facetsLoading}
        failedToLoad={facetsFailed}
        onRetry={() => void refetchFacets()}
        emptyMessage={emptyMessage}
      />
    ),
  });

  const registerLabels = [
    t("County"),
    t("Municipality"),
    t("Industry code"),
  ].map((label) => label.toLowerCase());
  const contractChipName = (facet: ContractFieldFacetDto) =>
    registerLabels.includes(facet.name.toLowerCase())
      ? `${facet.name} (${t("from your contracts")})`
      : facet.name;

  const contractDefinition = (facet: ContractFieldFacetDto): Definition => {
    const key = contractFilterKey(facet.fieldId);
    const chipName = contractChipName(facet);
    if (RANGE_KINDS.includes(facet.kind)) {
      const lowest = value.lowestValues[facet.fieldId];
      const isPercentage = facet.kind === ContractFieldFilterKindDto.Percentage;
      return {
        key,
        group: "contracts",
        label: facet.name,
        chipName: chipName,
        chipValue: t("at least {{value}}", { value: isPercentage ? `${lowest} %` : lowest }),
        editor: (
          <LowestValueInput
            label={facet.name}
            value={lowest ?? null}
            isPercentage={isPercentage}
            onChange={(next) => {
              const others = withoutFilter(value, key).lowestValues;
              onChange({
                ...value,
                lowestValues:
                  next === null ? others : { ...others, [facet.fieldId]: next },
              });
            }}
          />
        ),
      };
    }
    if (facet.kind === ContractFieldFilterKindDto.Date) {
      const period = value.periods[facet.fieldId] ?? EMPTY_PERIOD;
      return {
        key,
        group: "contracts",
        label: facet.name,
        chipName: chipName,
        chipValue: periodLabel(period),
        editor: (
          <PeriodInput
            label={facet.name}
            period={period}
            onChange={(next) =>
              onChange({
                ...value,
                periods: { ...value.periods, [facet.fieldId]: next },
              })
            }
          />
        ),
      };
    }
    const options = facet.values.map((option) => ({
      value: option,
      label:
        facet.kind !== ContractFieldFilterKindDto.YesNo
          ? option
          : option === "true"
            ? t("Yes")
            : t("No"),
    }));
    return listDefinition(
      key,
      "contracts",
      facet.name,
      value.contractFields[facet.fieldId] ?? [],
      options,
      (chosen) =>
        onChange({
          ...value,
          contractFields: { ...value.contractFields, [facet.fieldId]: chosen },
        }),
      t("No contract has a value in this field yet"),
      chipName,
    );
  };

  const relationChipParts = [
    value.relations.length > 0 &&
      t("has {{relations}}", {
        relations: value.relations.map((r) => relationLabels[r]).join(", "),
      }),
    value.withoutRelations.length > 0 &&
      t("has not {{relations}}", {
        relations: value.withoutRelations
          .map((r) => relationLabels[r])
          .join(", "),
      }),
  ].filter(Boolean);

  const definitions: Definition[] = [
    {
      key: "relations",
      group: "relation",
      label: t("Relation to us"),
      chipName: t("Relation to us"),
        chipValue: relationChipParts.join(" · "),
      editor: (
        <RelationFilter
          relations={value.relations}
          withoutRelations={value.withoutRelations}
          onChange={(change) => onChange({ ...value, ...change })}
        />
      ),
    },
    listDefinition(
      "counties",
      "register",
      t("County"),
      value.counties,
      countyOptions,
      (counties) => onChange({ ...value, counties }),
      t("No suppliers with a registered business address yet"),
    ),
    listDefinition(
      "municipalities",
      "register",
      t("Municipality"),
      value.municipalities,
      municipalityOptions,
      (municipalities) => onChange({ ...value, municipalities }),
      t("No suppliers with a registered business address yet"),
    ),
    listDefinition(
      "naceCodes",
      "register",
      t("Industry code"),
      value.naceCodes,
      naceOptions,
      (naceCodes) => onChange({ ...value, naceCodes }),
      t("No suppliers with a registered industry code yet"),
    ),
    ...(facetsResponse?.filtersOnContractEndDate
      ? [
          {
            key: "endDate" as const,
            group: "contracts" as const,
            label: t("End date"),
            chipName: t("End date"),
        chipValue: periodLabel(value.endDate),
            editor: (
              <PeriodInput
                label={t("End date")}
                period={value.endDate}
                onChange={(endDate) => onChange({ ...value, endDate })}
              />
            ),
          },
        ]
      : []),
    ...(facetsResponse?.contractFieldFacets ?? []).map(contractDefinition),
  ];

  const chosen = definitions.filter((definition) =>
    hasFilterValue(value, definition.key),
  );
  const pickedDefinition = definitions.find(
    (definition) => definition.key === picked,
  );
  const groups: { group: Group; heading?: string }[] = [
    { group: "relation" },
    {
      group: "register",
      heading: t("From the Brønnøysund Register Centre") as string,
    },
    { group: "contracts", heading: t("From your contracts") as string },
  ];

  const closeFilterMenu = () => {
    filterMenu.onClose();
    setPicked(null);
  };

  return (
    <Stack spacing="2">
      <Flex gap="2" align="center">
        {search}
        <Popover
          isOpen={filterMenu.isOpen}
          onOpen={filterMenu.onOpen}
          onClose={closeFilterMenu}
          placement="bottom-start"
          isLazy
        >
          <PopoverTrigger>
            <Button
              size={size}
              variant="outline"
              flexShrink={0}
              leftIcon={<Icon as={FiFilter} />}
            >
              {t("Add filter")}
            </Button>
          </PopoverTrigger>
          <Portal>
            <PopoverContent w="xs" maxW="calc(100vw - 32px)">
              <PopoverBody p="2" maxH="96" overflowY="auto">
                {pickedDefinition ? (
                  <Stack spacing="3">
                    <Button
                      size="xs"
                      variant="link"
                      colorScheme="teal"
                      alignSelf="flex-start"
                      leftIcon={<Icon as={FiChevronLeft} />}
                      onClick={() => setPicked(null)}
                    >
                      {t("All filters")}
                    </Button>
                    <Text fontSize="sm" fontWeight="semibold">
                      {pickedDefinition.label}
                    </Text>
                    {pickedDefinition.editor}
                  </Stack>
                ) : (
                  <Stack
                    spacing="2"
                    role="menu"
                    aria-label={t("Filter") as string}
                  >
                    {groups.map(({ group, heading }) => {
                      const items = definitions.filter(
                        (definition) => definition.group === group,
                      );
                      if (items.length === 0) return null;
                      return (
                        <Box key={group}>
                          {heading && (
                            <Text
                              px="2"
                              pt="1"
                              fontSize="xs"
                              fontWeight="bold"
                              textTransform="uppercase"
                              color="smMuted"
                            >
                              {heading}
                            </Text>
                          )}
                          {items.map((definition) => (
                            <Button
                              key={definition.key}
                              role="menuitem"
                              size="sm"
                              variant="ghost"
                              w="full"
                              justifyContent="flex-start"
                              fontWeight="normal"
                              onClick={() => setPicked(definition.key)}
                            >
                              {definition.label}
                            </Button>
                          ))}
                        </Box>
                      );
                    })}
                    {facetsFailed && (
                      <Flex align="center" gap="2" px="2">
                        <Text fontSize="xs" color="smError">
                          {t("Could not load the filter choices. Try again.")}
                        </Text>
                        <Button
                          size="xs"
                          variant="link"
                          colorScheme="teal"
                          onClick={() => refetchFacets()}
                        >
                          {t("Try again")}
                        </Button>
                      </Flex>
                    )}
                  </Stack>
                )}
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </Popover>
      </Flex>
      {chosen.length > 0 && (
        <Flex gap="2" rowGap="3" wrap="wrap" align="center">
          {chosen.map((definition) => (
            <Popover
              key={definition.key}
              isOpen={editing === definition.key}
              onClose={() => setEditing(null)}
              placement="bottom-start"
              isLazy
            >
              <PopoverAnchor>
                <RemovableFilterChip
                  label={definition.chipName}
                  value={definition.chipValue}
                  onOpen={() => setEditing(definition.key)}
                  onRemove={() =>
                    onChange(withoutFilter(value, definition.key))
                  }
                />
              </PopoverAnchor>
              <Portal>
                <PopoverContent w="xs" maxW="calc(100vw - 32px)">
                  <PopoverBody p="3" maxH="96" overflowY="auto">
                    <Stack spacing="3">
                      <Text fontSize="sm" fontWeight="semibold">
                        {definition.label}
                      </Text>
                      {definition.editor}
                    </Stack>
                  </PopoverBody>
                </PopoverContent>
              </Portal>
            </Popover>
          ))}
          <Button
            size="sm"
            variant="link"
            colorScheme="teal"
            onClick={() => onChange(NO_SUPPLIER_FILTERS)}
          >
            {t("Reset filters")}
          </Button>
        </Flex>
      )}
    </Stack>
  );
};
